import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, Drawer, GlassTable, StatusPill } from '@jumpstone/ui-react';
import { ScreenHeader } from '../../components/common/screen_header';
import {
  UPDATE_REQUEST_KIND_KEY,
  UPDATE_REQUEST_STATUS_KEY,
  UPDATE_REQUEST_STATUS_PILL,
} from '../../lib/label_keys';
import { useUpdateRequests } from '../../data/use_update_requests';
import { format_date } from '../../lib/format';

/** Employee-submitted record changes, reviewed as an old → new diff. */
export function UpdateRequestsScreen(): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { requests, approve, reject } = useUpdateRequests();
  const [open_id, set_open_id] = useState<string | null>(null);
  const open_request =
    requests.find((request) => request.id === open_id) ?? null;

  const close = (): void => {
    set_open_id(null);
  };

  return (
    <>
      <ScreenHeader
        title={t('update_requests.title')}
        description={t('update_requests.subtitle')}
      />

      <GlassTable>
        <table className="nv-table">
          <thead>
            <tr>
              <th scope="col">{t('update_requests.column_type')}</th>
              <th scope="col">{t('update_requests.column_requester')}</th>
              <th scope="col">{t('update_requests.column_submitted')}</th>
              <th scope="col">{t('update_requests.column_status')}</th>
              <th scope="col">
                <span className="sr-only">{t('common.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <tr key={request.id}>
                <td className="font-semibold">
                  {t(UPDATE_REQUEST_KIND_KEY[request.kind])}
                </td>
                <td>{request.requester_name}</td>
                <td>{format_date(request.submitted_on, locale)}</td>
                <td>
                  <StatusPill
                    status={UPDATE_REQUEST_STATUS_PILL[request.status]}
                    label={t(UPDATE_REQUEST_STATUS_KEY[request.status])}
                  />
                </td>
                <td className="text-end">
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => {
                      set_open_id(request.id);
                    }}
                  >
                    {t('update_requests.view_diff')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </GlassTable>

      <Drawer
        open={open_request !== null}
        onClose={close}
        title={
          open_request === null
            ? t('update_requests.title')
            : t(UPDATE_REQUEST_KIND_KEY[open_request.kind])
        }
        // The body's first (and often only) focusable control is the
        // mutating Approve button -- the default `initialFocus="body"`
        // would autofocus it, so a stray Enter keypress right after
        // opening the drawer approves the request. `"close"` keeps focus
        // on the header close button instead, same as every other
        // Drawer consumer had before #23605's body-first default.
        initialFocus="close"
      >
        {open_request && (
          <>
            <p className="text-[13px] text-[color:var(--ink-muted)] mb-3">
              {t('update_requests.requested_by', {
                name: open_request.requester_name,
                date: format_date(open_request.submitted_on, locale),
              })}
            </p>

            <div className="flex flex-col gap-2 mb-4">
              <div className="wp-diff-old flex justify-between gap-3 rounded-lg p-2.5 text-[13px]">
                <span className="wp-fieldlabel">
                  {t('update_requests.old_value')}
                </span>
                <span>{open_request.old_value}</span>
              </div>
              <div className="wp-diff-new flex justify-between gap-3 rounded-lg p-2.5 text-[13px]">
                <span className="wp-fieldlabel">
                  {t('update_requests.new_value')}
                </span>
                <span>{open_request.new_value}</span>
              </div>
            </div>

            {open_request.status === 'in_review' ? (
              <div className="flex gap-2">
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => {
                    approve(open_request.id);
                    close();
                  }}
                >
                  {t('common.approve')}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    reject(open_request.id);
                    close();
                  }}
                >
                  {t('common.reject')}
                </Button>
              </div>
            ) : (
              <StatusPill
                status={UPDATE_REQUEST_STATUS_PILL[open_request.status]}
                label={t(UPDATE_REQUEST_STATUS_KEY[open_request.status])}
              />
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
